import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Send, Loader2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";

export const PHONES = ["+2250708133608", "+2250546080886", "+2250100030470"];
const fmt = (p) => p.replace(/^\+225(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/, "+225 $1 $2 $3 $4 $5");
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

function WhatsAppList() {
  return (
    <div className="space-y-3">
      {PHONES.map((p, i) => (
        <a key={p} href={`https://wa.me/${p.replace("+", "")}`} target="_blank" rel="noopener noreferrer"
          data-testid={`contact-whatsapp-${i}`}
          className="group flex items-center gap-4 bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 hover:border-[#25D366] hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
          <span className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0"><MessageCircle size={20} /></span>
          <span className="flex-1">
            <span className="block meta-label text-slate-500">WhatsApp</span>
            <span className="block font-sub text-lg font-semibold text-[#0A0A0A]">{fmt(p)}</span>
          </span>
          <span className="meta-label text-[#25D366] opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">Écrire →</span>
        </a>
      ))}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! La rédaction vous répondra rapidement.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err.response?.status === 429 ? "Trop de messages envoyés, réessayez plus tard." : "Vérifiez les champs (e-mail valide, message de 5 caractères minimum).");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 font-sub text-sm outline-none focus:border-[#E11D48] focus:ring-1 focus:ring-[#E11D48] transition-colors";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={form.name} onChange={set("name")} placeholder="Votre nom *" className={field} data-testid="contact-name-input" />
        <input required type="email" value={form.email} onChange={set("email")} placeholder="Votre e-mail *" className={field} data-testid="contact-email-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input value={form.phone} onChange={set("phone")} placeholder="Téléphone (facultatif)" className={field} data-testid="contact-phone-input" />
        <input required value={form.subject} onChange={set("subject")} placeholder="Objet *" className={field} data-testid="contact-subject-input" />
      </div>
      <textarea required minLength={5} rows={6} value={form.message} onChange={set("message")} placeholder="Votre message *" className={field} data-testid="contact-message-input" />
      <button type="submit" disabled={sending} data-testid="contact-submit-button"
        className="inline-flex items-center gap-2 bg-[#E11D48] hover:bg-[#BE123C] text-white font-sub font-semibold px-6 py-3 rounded-full transition-colors disabled:opacity-60">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer le message
      </button>
    </form>
  );
}

export default function ContactPage() {
  useEffect(() => { document.title = "Contact — Le Véridique"; window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-24 lg:py-16" data-testid="contact-page">
        <p className="meta-label text-[#E11D48] mb-3">Nous écrire</p>
        <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0A0A] mb-4">Contact</h1>
        <p className="font-body text-base text-slate-600 max-w-2xl text-justify mb-12">
          Une question, une information à signaler, une correction, une proposition d'article ou de partenariat ? Écrivez-nous directement sur WhatsApp ou via le formulaire ci-dessous.
        </p>
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          <section className="lg:col-span-2">
            <h2 className="font-headline text-lg font-bold text-[#0A0A0A] mb-4">Sur WhatsApp</h2>
            <WhatsAppList />
            <Link to="/faq" data-testid="contact-faq-link" className="mt-8 flex items-center gap-2 font-sub text-sm text-slate-600 hover:text-[#E11D48]">
              <HelpCircle size={16} /> Consultez d'abord notre foire aux questions
            </Link>
          </section>
          <section className="lg:col-span-3">
            <h2 className="font-headline text-lg font-bold text-[#0A0A0A] mb-4">Formulaire de message</h2>
            <ContactForm />
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
